import {
  deviceZone,
  formatCountdown,
  formatDateTime,
  formatDetail,
  formatClock,
  formatHm,
  formatSmart,
  isValidZone,
  localDate,
  serverNow,
  timeLocaleOf,
} from '~/utils/time'

/** 设备时区 cookie：客户端插件写入，服务端渲染按它格式化，保证首屏与水合一致 */
export const DEVICE_ZONE_COOKIE = 'device_tz'

export type LocalTimeMode = 'smart' | 'detail' | 'datetime' | 'date' | 'clock' | 'hm'

/**
 * 玩家端时刻展示上下文：按**设备本地时区**显示（规范 6.2）。
 *
 * SSR 规则：服务器不按自己的时区（PM2 TZ=UTC）格式化时间。
 * - 首次访问没有时区 cookie：服务端只输出占位（zone = null），挂载后按设备时区填入；
 * - 之后的访问：服务端按 cookie 里的设备时区渲染；水合时沿用同一时区与同一「此刻」（payload），
 *   保证无水合不一致；挂载后切换到实时的设备时区与服务端校正时钟。
 */
export function useTimeDisplay() {
  const nuxtApp = useNuxtApp()
  const { t, locale } = useI18n()

  const ssrZone = useState<string | null>('time:ssr-zone', () => {
    const fromCookie = useCookie<string | null>(DEVICE_ZONE_COOKIE).value
    return isValidZone(fromCookie) ? fromCookie : null
  })
  const ssrNow = useState<number>('time:ssr-now', () => serverNow())

  // 客户端非水合阶段（如抽屉、路由切换后新挂载）直接按实时口径渲染
  const live = ref(import.meta.client && !nuxtApp.isHydrating)
  if (import.meta.client && !live.value) {
    onMounted(() => {
      live.value = true
    })
  }

  const { now: tick } = useServerClock(30_000)

  /**
   * 渲染时区：undefined = 运行环境默认时区（客户端取不到 IANA 名时退回它，仍是设备时区）；
   * null = 服务端没有可用的设备时区，只能输出占位。
   */
  const zone = computed<string | undefined | null>(() => {
    if (live.value) return deviceZone() ?? undefined
    return ssrZone.value
  })
  const now = computed(() => (live.value ? tick.value : ssrNow.value))
  const timeLocale = computed(() => timeLocaleOf(String(locale.value)))

  /** 按模式格式化一个时刻（毫秒）；zone 为 null（SSR 首访）时返回 null，调用方输出占位 */
  const format = (ms: number, mode: LocalTimeMode = 'smart'): string | null => {
    const tz = zone.value
    if (tz === null) return null
    switch (mode) {
      case 'detail':
        return formatDetail(ms, tz)
      case 'datetime':
        return formatDateTime(ms, tz)
      case 'date':
        return localDate(ms, tz)
      case 'clock':
        return formatClock(ms, tz)
      case 'hm':
        return formatHm(ms, tz)
      default:
        return formatSmart(ms, {
          tz,
          now: now.value,
          locale: timeLocale.value,
          today: t('common.time.today'),
          yesterday: t('common.time.yesterday'),
        })
    }
  }

  /** 倒计时文案（天数走 i18n） */
  const countdown = (ms: number) =>
    formatCountdown(ms, {
      locale: timeLocale.value,
      days: n => t('common.time.days', { n }),
    })

  return { live, zone, now, timeLocale, format, countdown }
}
